import {
  ITEM_NOTE_MAX_LENGTH,
  useItemNotesRemovedByFlag,
} from '@/components/page-specific/catering/common/helpers/useItemNotesRemovedByFlag'
import { MenuItemOptionType } from '@/generated/graphql'
import { DietaryRow } from '@fusion/dietary/row'
import { Person } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'

import type { TFunction } from 'i18next'
import { sortBy } from 'lodash'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import type { MenuItem, PriceAudience } from '../../domain/menu-item'
import type { MenuItemOption, MenuOptionItem } from '../../domain/menu-item-option'
import type { MenuVendor } from '../../domain/menu-vendor'
import type { SelectedOptionItem } from './menu-item-modal'
import { NumberOptions } from './number-options'
import OptionGuidanceText from './option-guidance-text'
import { PackagingTag } from './packaging-tag'
import StyledChip from './styled-chip'
import { TickOptions } from './tick-options'

type Props = {
  vendor: Pick<MenuVendor, 'currency'>
  item: MenuItem
  quantity: number
  selectedOptions: Map<string, Map<string, SelectedOptionItem>>
  selectOptionItem: (option: MenuItemOption, item: MenuOptionItem, quantity?: number) => void
  onAllergyButtonClick: () => void
  note: string
  noteIsEditable: boolean
  setNote: (note: string) => void
  taxAcronym: string
  showNotes?: boolean
  pricesIncTax?: boolean
  priceAudience?: PriceAudience
}

const HotOrColdTag: React.FC<{ isHot: boolean; t: TFunction }> = ({ isHot, t }) =>
  match(isHot)
    .with(true, () => (
      <Chip
        size="small"
        label={t('marketplace:hot')}
        sx={{
          textDecoration: 'uppercase',
          backgroundColor: '#F4E9EC',
          color: '#781C38',
        }}
      />
    ))
    .with(false, () => (
      <Chip
        size="small"
        label={t('marketplace:cold')}
        sx={{
          textDecoration: 'uppercase',
          backgroundColor: '#EBF7FB',
          color: '#3AADD8',
        }}
      />
    ))
    .exhaustive()

export const OverviewTab: React.FC<Props> = ({
  vendor,
  item,
  quantity,
  selectedOptions,
  selectOptionItem,
  onAllergyButtonClick,
  note,
  noteIsEditable,
  setNote,
  taxAcronym,
  showNotes,
  pricesIncTax,
  priceAudience,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const orderedOptions = sortBy(item.options, (options) => options?.position)
  const notesRemovedByFlag = useItemNotesRemovedByFlag()
  // Notes are basket-specific — hidden in admin preview (showNotes false) as
  // well as when the flag removes them.
  const showItemNotes = showNotes !== false && !notesRemovedByFlag

  const handleNoteChange = (note: string) => {
    setNote(note)
  }
  let servesAmount = `${item.servings}`
  if (item.isPricePerHead) {
    servesAmount = `${item.minQty}`
    if (item.minQty > 1) servesAmount += '+'
  }

  return (
    <>
      <Box px={3} py={1}>
        {isMobileScreen && (
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {item.name}
          </Typography>
        )}

        <Typography variant="body1" fontWeight="400" margin="0 0 0.75em" whiteSpace="pre-wrap">
          {item.description}
        </Typography>

        <Button size="small" onClick={onAllergyButtonClick}>
          {t('cc:haveAnAllergy')}
        </Button>
      </Box>

      <Box
        sx={{
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: 'divider',
        }}>
        <Stack spacing={1}>
          {item.dietaries.length > 0 && (
            <Box p={2}>
              <DietaryRow allergens={[]} dietaries={item.dietaries} />
            </Box>
          )}

          <Box p={2}>
            <DietaryRow
              isComboItem={Boolean(item.isCombo)}
              allergens={item.allergens}
              dietaries={[]}
              subAllergens={item.subAllergens}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        px={1}
        sx={{
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: 'divider',
        }}>
        <Stack spacing={1}>
          {typeof item.isHot === 'boolean' && (
            <Box p={1}>
              <HotOrColdTag isHot={item.isHot} t={t} />
            </Box>
          )}

          <Box p={1}>
            <Stack spacing={1} direction="row">
              <StyledChip label={t('marketplace:serves', { servesAmount })} icon={<Person />} />
              <PackagingTag packaging={item.packaging} t={t} />
            </Stack>
          </Box>
        </Stack>
      </Box>

      {orderedOptions.map((option) => {
        if (!option?.name) return null

        const hasIncTaxFallback =
          !pricesIncTax &&
          priceAudience === 'vendor' &&
          option.items.some((item) => item.vendorPriceExTax == null && item.vendorPrice != null)
        const hasExTaxPrice = option.items.some((item) => item.vendorPriceExTax != null)
        const showPriceBasisPerItem = hasIncTaxFallback && hasExTaxPrice
        const priceBasisLabel = showPriceBasisPerItem
          ? `${t('marketplace:ex', { taxAcronym })} / ${t('marketplace:inc', { taxAcronym })}`
          : t(pricesIncTax || hasIncTaxFallback ? 'marketplace:inc' : 'marketplace:ex', {
              taxAcronym,
            })

        return (
          <Box key={option._id}>
            <Box
              py={2}
              px={3}
              bgcolor="rgb(250, 250, 250)"
              sx={{
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                borderBottomColor: 'divider',
              }}>
              <Stack justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {option.name}
                </Typography>
                <OptionGuidanceText option={option} quantity={quantity} />
              </Stack>
              <Stack direction="row-reverse">
                <Typography color="text.secondary" variant="caption">
                  ({t('marketplace:prices')} {priceBasisLabel})
                </Typography>
              </Stack>
            </Box>
            {match(option.optionType)
              .with(MenuItemOptionType.Ticks, () => (
                <TickOptions
                  option={option}
                  currency={vendor.currency}
                  selectedOptionItems={selectedOptions.get(option._id)}
                  selectOptionItem={selectOptionItem}
                  pricesIncTax={pricesIncTax}
                  priceAudience={priceAudience}
                  showPriceBasisPerItem={showPriceBasisPerItem}
                  taxAcronym={taxAcronym}
                />
              ))
              .with(MenuItemOptionType.Numbers, () => (
                <NumberOptions
                  option={option}
                  currency={vendor.currency}
                  quantity={quantity}
                  selectedOptionItems={selectedOptions.get(option._id)}
                  selectOptionItem={selectOptionItem}
                  pricesIncTax={pricesIncTax}
                  priceAudience={priceAudience}
                  showPriceBasisPerItem={showPriceBasisPerItem}
                  taxAcronym={taxAcronym}
                />
              ))
              .otherwise(() => null)}
          </Box>
        )
      })}

      {showItemNotes ? (
        <Box py={2} px={3}>
          <TextField
            label={t('marketplace:addNotes')}
            multiline
            minRows={2}
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: ITEM_NOTE_MAX_LENGTH },
            }}
            disabled={!noteIsEditable}
            value={note}
            onChange={(e) => handleNoteChange(e.target.value.slice(0, ITEM_NOTE_MAX_LENGTH))}
            helperText={`${note.length}/${ITEM_NOTE_MAX_LENGTH}`}
          />
        </Box>
      ) : null}

      {showItemNotes ? (
        <Box py={2} px={3}>
          <Typography variant="caption">
            <b>{t('marketplace:importantInformationNotes')}:</b>{' '}
            {t('marketplace:importantInformationNotesText')}
          </Typography>
        </Box>
      ) : null}

      {item.minQty > 1 && (
        <Box bgcolor="rgb(250, 250, 250)" py={1}>
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            {t('marketplace:youMustOrderMinQty', { minQty: item.minQty })}
          </Typography>
        </Box>
      )}
    </>
  )
}
